import { Controller } from "@hotwired/stimulus"

const STEPS = [
  {
    id: "fund-count",
    title: "Nombre de fonds suivis",
    body: "Ce chiffre correspond au nombre de fonds actuellement inclus dans votre abonnement."
  },
  {
    id: "current-cost",
    title: "Coût actuel",
    body: "Ce montant correspond au coût actuel de votre abonnement Vigie des fonds."
  },
  {
    id: "pricing-tiers",
    title: "Grille tarifaire",
    body: "Ces cartes présentent le tarif annuel HT/an par fonds selon le nombre de fonds sélectionnés. Plus le nombre de fonds est élevé, plus le coût unitaire diminue."
  },
  {
    id: "all-funds",
    title: "Abonnement à tous les fonds",
    body: "Cette option permet de suivre l’ensemble des fonds dont EOS Allocations rencontre trimestriellement les gérants. Cette liste évolue selon les analyses d'EOS Allocations."
  },
  {
    id: "extra-funds",
    title: "Ajouter un fonds hors liste",
    body: "Ce bouton permet de demander le suivi d’un fonds qui n’est pas encore présent dans la liste standard. Cette demande nécessite ensuite une validation par EOS avant mise en place."
  },
  {
    id: "search-funds",
    title: "Recherche dans la liste",
    body: "Ce champ permet de retrouver rapidement un fonds de la liste à partir de son nom, de sa société de gestion ou de son code ISIN."
  },
  {
    id: "fund-categories",
    title: "Catégories dépliables",
    body: "Les catégories peuvent être dépliées pour afficher les fonds qu’elles contiennent et sélectionner ceux que vous souhaitez suivre."
  },
  {
    id: "submit-subscription",
    title: "Modifier l’abonnement",
    body: "Une fois votre sélection faite, utilisez ce bouton pour lancer la modification. Les changements sont encadrés : un délai minimum de 3 mois est appliqué entre deux modifications, avec un maximum de 2 modifications sur 12 mois."
  }
]

export default class extends Controller {
  connect() {
    this.currentStepIndex = 0
    this.highlightedElement = null
    this.demoCategoryElement = null
    this.boundReposition = this.reposition.bind(this)
    this.boundNext = this.next.bind(this)
    this.boundClose = this.close.bind(this)

    requestAnimationFrame(() => {
      this.build()
      this.showStep(0)
    })
  }

  disconnect() {
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
    this.cleanupHighlight()

    if (this.nextButton) this.nextButton.removeEventListener("click", this.boundNext)
    if (this.closeButton) this.closeButton.removeEventListener("click", this.boundClose)
    if (this.stopLink) this.stopLink.removeEventListener("click", this.boundClose)

    if (this.overlay) this.overlay.remove()
    if (this.card) this.card.remove()
  }

  next(event) {
    if (event) event.preventDefault()

    this.cleanupStepEffects()

    if (this.currentStepIndex >= STEPS.length - 1) {
      this.close()
      return
    }

    this.showStep(this.currentStepIndex + 1)
  }

  close(event) {
    if (event) event.preventDefault()
    this.cleanupStepEffects()
    this.cleanupHighlight()

    if (this.overlay) this.overlay.classList.add("hidden")
    if (this.card) this.card.classList.add("hidden")

    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  build() {
    this.overlay = document.createElement("div")
    this.overlay.className = "fixed inset-0 z-[120] bg-slate-950/45"

    this.card = document.createElement("div")
    this.card.className = "fixed z-[130] hidden w-[320px] border border-slate-200 bg-white p-5 shadow-2xl"

    this.card.innerHTML = `
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500" data-guided-tour-role="counter"></div>
          <h3 class="mt-2 text-base font-semibold text-slate-900" data-guided-tour-role="title"></h3>
        </div>

        <button type="button"
                class="shrink-0 text-sm font-medium text-slate-500 transition hover:text-slate-700"
                data-guided-tour-role="close">
          Fermer
        </button>
      </div>

      <p class="mt-3 text-sm leading-6 text-slate-600" data-guided-tour-role="body"></p>

      <div class="mt-5 flex items-center justify-between gap-4">
        <a href="#"
           class="text-sm text-slate-500 underline underline-offset-2 transition hover:text-slate-700"
           data-guided-tour-role="stop">
          Interrompre le tutoriel
        </a>

        <button type="button"
                class="inline-flex items-center border border-transparent bg-oxford-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-oxford-800"
                data-guided-tour-role="next">
          Suivant
        </button>
      </div>
    `

    document.body.appendChild(this.overlay)
    document.body.appendChild(this.card)

    this.counterElement = this.card.querySelector('[data-guided-tour-role="counter"]')
    this.titleElement = this.card.querySelector('[data-guided-tour-role="title"]')
    this.bodyElement = this.card.querySelector('[data-guided-tour-role="body"]')
    this.closeButton = this.card.querySelector('[data-guided-tour-role="close"]')
    this.nextButton = this.card.querySelector('[data-guided-tour-role="next"]')
    this.stopLink = this.card.querySelector('[data-guided-tour-role="stop"]')

    this.closeButton.addEventListener("click", this.boundClose)
    this.nextButton.addEventListener("click", this.boundNext)
    this.stopLink.addEventListener("click", this.boundClose)
  }

  showStep(index) {
    const step = STEPS[index]
    if (!step) return

    const element = this.resolveStepElement(step.id)
    if (!element) {
      if (index >= STEPS.length - 1) {
        this.close()
      } else {
        this.showStep(index + 1)
      }
      return
    }

    this.currentStepIndex = index
    this.cleanupHighlight()
    this.applyStepEffects(step.id)

    element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })

    window.setTimeout(() => {
      this.highlight(element)
      this.counterElement.textContent = `Étape ${index + 1} / ${STEPS.length}`
      this.titleElement.textContent = step.title
      this.bodyElement.textContent = step.body
      this.nextButton.textContent = index === STEPS.length - 1 ? "Terminer" : "Suivant"

      this.overlay.classList.remove("hidden")
      this.card.classList.remove("hidden")
      this.reposition()
      this.nextButton.focus()

      window.addEventListener("resize", this.boundReposition)
      window.addEventListener("scroll", this.boundReposition, true)
    }, 180)
  }

  resolveStepElement(stepId) {
    if (stepId === "fund-categories") {
      return this.middleCategoryElement() || document.querySelector('[data-guided-tour-step="fund-categories"]')
    }

    return document.querySelector(`[data-guided-tour-step="${stepId}"]`)
  }

  applyStepEffects(stepId) {
    if (stepId !== "fund-categories") return

    const category = this.middleCategoryElement()
    if (!category) return

    this.demoCategoryElement = category
    category.open = true
  }

  cleanupStepEffects() {
    if (this.demoCategoryElement) {
      this.demoCategoryElement.open = false
      this.demoCategoryElement = null
    }
  }

  middleCategoryElement() {
    const categories = Array.from(document.querySelectorAll('[data-subscription-all-funds-target="category"]'))
      .filter((element) => !element.classList.contains("hidden"))

    if (categories.length === 0) return null

    return categories[Math.floor(categories.length / 2)]
  }

  highlight(element) {
    this.highlightedElement = element
    element.classList.add("relative", "z-[125]", "ring-4", "ring-oxford-900", "ring-offset-4", "ring-offset-white")
  }

  cleanupHighlight() {
    if (!this.highlightedElement) return

    this.highlightedElement.classList.remove("relative", "z-[125]", "ring-4", "ring-oxford-900", "ring-offset-4", "ring-offset-white")
    this.highlightedElement = null
  }

  reposition() {
    if (!this.highlightedElement || !this.card) return

    const rect = this.highlightedElement.getBoundingClientRect()
    const cardRect = this.card.getBoundingClientRect()
    const margin = 16
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let top = rect.bottom + margin
    if (top + cardRect.height > viewportHeight - margin) {
      top = rect.top - cardRect.height - margin
    }

    top = Math.max(margin, top)

    let left = rect.left
    if (left + cardRect.width > viewportWidth - margin) {
      left = viewportWidth - cardRect.width - margin
    }

    left = Math.max(margin, left)

    this.card.style.top = `${top}px`
    this.card.style.left = `${left}px`
  }
}
